import { SOCIAL } from '../../config/env.ts';
import { useI18n } from '../../i18n/index.tsx';

// The networks' own marks (Simple Icons, 24-unit grid). Each viewBox frames the glyph alone, centred, so it can sit
// raised on its tile; Telegram's is just the paper plane, the tile itself being the blue disc of its logo.
const MARKS = {
  telegram: {
    box: '3.4 5.4 15 15',
    d: 'M16.906 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z',
  },
  x: {
    box: '-1.5 -0.5 27 25',
    d: 'M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z',
  },
} as const;
const NAMES = { telegram: 'Telegram', x: 'X (Twitter)' } as const;
export type Network = keyof typeof MARKS;

/** One network's 3D app-icon tile: a glossy, bevelled squircle with a visible edge, its mark raised above the face. */
function Tile({ n }: { n: Network }) {
  return (
    <span className="s3-body" aria-hidden="true">
      <span className="s3-face">
        <svg className="s3-glyph" viewBox={MARKS[n].box}>
          <defs>
            <linearGradient id={`s3g-${n}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#ffffff" />
              <stop offset="1" stopColor={n === 'x' ? '#c9cbd6' : '#dfe9ff'} />
            </linearGradient>
          </defs>
          <path d={MARKS[n].d} fill={`url(#s3g-${n})`} />
        </svg>
      </span>
    </span>
  );
}

/** The project's Telegram and X as 3D icon tiles that open in a new tab. */
export function SocialLinks({ className = '' }: { className?: string }) {
  const { t } = useI18n();
  return (
    <div className={`social ${className}`.trim()} data-testid="social">
      {(Object.keys(MARKS) as Network[]).map((n) => {
        const href = SOCIAL[n];
        return href ? (
          <a key={n} className={`s3 ${n}`} href={href} target="_blank" rel="noopener noreferrer" aria-label={NAMES[n]} title={NAMES[n]} data-lean="vars">
            <Tile n={n} />
          </a>
        ) : (
          // not announced yet: the tile keeps its place, dimmed, and says so
          <span
            key={n}
            className={`s3 ${n} soon`}
            role="link"
            aria-disabled="true"
            aria-label={`${NAMES[n]} · ${t('social.soon')}`}
            title={`${NAMES[n]} · ${t('social.soon')}`}
          >
            <Tile n={n} />
          </span>
        );
      })}
    </div>
  );
}
